<template>
  <main class="record-page" :class="{ 'legacy-0101': routeTag === '0101', 'has-record': Boolean(machine) && !locked }">
    <section class="record-shell">
      <router-link class="back" to="/maintunits">← Maintained units</router-link>
      <a v-if="routeTag || machineToken" class="staff-link" :href="staffLink">Staff machine view →</a>

      <p v-if="checkingEnrollment" class="loading">Checking machine record…</p>

      <BasicMachineLanding
        v-else-if="kind === 'basic'"
        :scan-key="machineToken || routeTag"
        :by-token="Boolean(machineToken)"
        :tag="routeTag"
        :label="scanLabel"
        :public-type="scanType"
        :initial-sheet="sheet"
        @fleet-session="openFleetRecords"
      />

      <section v-else-if="locked" class="unlock-card">
        <div class="shield" aria-hidden="true"><span></span></div>
        <p class="eyebrow">{{ routeTag === '0101' ? 'Private machine portal' : 'Protected customer records' }}</p>
        <h1>{{ routeTag === '0101' ? 'Protected machine record' : (routeTag ? `CFM# ${routeTag}` : 'Your machines') }}</h1>
        <p>{{ routeTag === '0101' ? "Enter the access PIN provided by Centerfire to view this machine's maintenance and service record." : 'Enter your customer PIN once to unlock every machine in your fleet.' }}</p>
        <form @submit.prevent="unlock"><label>{{ routeTag === '0101' ? 'Machine access PIN' : 'Customer PIN' }}<input v-model.trim="pin" type="password" inputmode="numeric" autocomplete="current-password" required autofocus /></label><button class="btn btn-primary" :disabled="busy">{{ busy ? 'Opening…' : (routeTag === '0101' ? 'Open machine record' : 'Open records') }}</button></form>
        <p class="status error" role="status">{{ error }}</p>
      </section>

      <p v-else-if="busy" class="loading">Loading machine record…</p>

      <template v-else-if="machine">
        <nav v-if="fleet.length > 1" class="fleet-nav" aria-label="Your machines">
          <p>Your machines</p>
          <button v-for="item in fleet" :key="item.tag" :class="{ active: item.tag === machine.identity.tag }" @click="openFleetMachine(item.tag)"><strong>CFM# {{ item.tag }}</strong><span>{{ item.publicLabel }}</span></button>
        </nav>

        <!-- The record formatting below is the pre-rebuild dashboard (restored 2026-08-30 from
             ec39f6f at Daniel's direction): centered greeting hero, bordered oil card with the
             filled remaining badge, open stat tiles, and bordered panels. The data underneath
             is the current portal API — only the clothes are the old ones. -->
        <article class="shell record-dashboard" aria-live="polite">
          <!-- The greeting is the hero. Without one the machine name stands in, uppercased. -->
          <h1 class="greeting-hero" :class="{ 'hero-machine': !machine.identity.greeting }">{{ machine.identity.greeting || machine.identity.publicLabel }}</h1>

          <header class="record-header">
            <div class="machine-identity">
              <p class="demo-label">{{ isDemo ? 'Example record · access verified' : 'Access verified' }}</p>
              <!-- Only when a greeting owns the hero; otherwise this would repeat the hero. -->
              <h2 v-if="machine.identity.greeting">{{ machine.identity.make }} {{ machine.identity.model }}</h2>
              <p>{{ machine.identity.publicLabel }}</p>
            </div>
            <div class="header-right">
              <div class="machine-number">
                <span>Centerfire machine number</span>
                <strong>{{ machine.identity.tag }}</strong>
              </div>
              <button v-if="!isDemo" class="btn btn-ghost lock-button" type="button" @click="lock">Lock record</button>
            </div>
          </header>

          <section v-if="machine.nextOilChange" class="oil-due-card" aria-labelledby="oil-due-title">
            <p>Next oil change</p>
            <h2 id="oil-due-title">{{ dueHeadline(machine.nextOilChange) }}</h2>
            <p v-if="dueSubline(machine.nextOilChange)">{{ dueSubline(machine.nextOilChange) }}</p>
            <span>{{ remainingText(machine.nextOilChange) }}</span>
            <p class="last-oil">{{ lastOilChangeText }}</p>
          </section>
          <section v-else class="oil-due-card oil-due-pending" aria-labelledby="oil-due-title">
            <p>Next oil change</p>
            <h2 id="oil-due-title">Being scheduled</h2>
            <p>We are confirming this machine's oil interval against the manufacturer's figures.</p>
            <span>Your first service visit sets the baseline</span>
            <p class="last-oil">{{ lastOilChangeText }}</p>
          </section>

          <section class="record-stats" aria-label="Current maintenance status">
            <div>
              <p>Current {{ meterUnit }}</p>
              <strong>{{ meterValue(machine.currentStatus.reading?.hours) }}</strong>
              <span v-if="machine.currentStatus.reading?.at">{{ formatDate(machine.currentStatus.reading.at) }} · {{ machine.currentStatus.reading.source === 'portal' ? 'you reported this' : 'verified' }}</span>
            </div>
            <!-- "Serviced" means our maintenance run — an oil change or a repair carries its own
                 date, so this tile pins to the last maintenance stop, date first. -->
            <div>
              <p>Last serviced</p>
              <strong>{{ machine.lastMaintenanceStop ? formatDate(machine.lastMaintenanceStop.at) : '—' }}</strong>
              <span>{{ machine.lastMaintenanceStop ? stopHours(machine.lastMaintenanceStop) : 'First visit sets the baseline' }}</span>
            </div>
            <div>
              <p>Program status</p>
              <strong>{{ machine.currentStatus.programStatus }}</strong>
              <span>{{ isDemo ? 'Example managed-maintenance record' : 'Managed maintenance by Centerfire' }}</span>
            </div>
          </section>

          <section class="record-grid">
            <article class="record-panel history-panel">
              <div class="panel-heading">
                <p>Recorded history</p>
                <h2>Service and repairs</h2>
              </div>

              <div class="history-entry">
                <p class="entry-type">Last service</p>
                <h3>{{ machine.lastService?.title || 'No service recorded yet' }}</h3>
                <p v-if="machine.lastService" class="entry-meta">{{ formatDate(machine.lastService.at) }} · {{ hours(machine.lastService.hours) }}</p>
                <p>{{ machine.lastService?.detail }}</p>
              </div>

              <div v-if="machine.previousRepair" class="history-entry">
                <p class="entry-type">Previous repair</p>
                <h3>{{ machine.previousRepair.title }}</h3>
                <p v-if="machine.previousRepair.at" class="entry-meta">{{ formatDate(machine.previousRepair.at) }} · {{ hours(machine.previousRepair.hours) }}</p>
                <p>{{ machine.previousRepair.detail }}</p>
              </div>
              <div v-else class="history-entry">
                <p class="entry-type">Previous repair</p>
                <h3>No completed repairs yet</h3>
                <p>Any repair we carry out will be recorded here with the date, the reading at the time, and what was done.</p>
              </div>

              <button class="btn btn-ghost history-button" type="button" aria-controls="customer-maintenance-history" :aria-expanded="String(historyOpen)" @click="toggleHistory">{{ historyOpen ? 'Hide history' : 'View history' }}</button>
            </article>

            <article class="record-panel upcoming-panel">
              <div class="panel-heading">
                <p>Upcoming service</p>
                <h2>{{ machine.upcomingService?.item || 'Intervals being confirmed' }}</h2>
              </div>
              <p v-if="machine.upcomingService" class="remaining">{{ remainingText(machine.upcomingService) }}</p>
              <p v-else>We are confirming this machine's service intervals against the manufacturer's figures. Your grease-run checkups continue on schedule in the meantime.</p>

              <div class="checkup-schedule">
                <p>{{ machine.greaseSchedule.frequency || 'Schedule being confirmed' }}</p>
                <h3>Next grease-run checkup</h3>
                <strong v-if="machine.greaseSchedule.nextGreaseDate">{{ formatDate(machine.greaseSchedule.nextGreaseDate) }}</strong>
                <span>Centerfire records every completed stop.</span>
              </div>

              <p v-if="machine.greaseSchedule.ownerActivity.count" class="owner-greases">
                <strong>{{ machine.greaseSchedule.ownerActivity.count }}</strong>
                {{ machine.greaseSchedule.ownerActivity.count === 1 ? 'greasing' : 'greasings' }} logged by you since our last stop
              </p>

              <div v-if="machine.greaseSchedule.recentStops.length" class="grease-cadence">
                <h3>Grease stops kept</h3>
                <ul>
                  <li v-for="stop in machine.greaseSchedule.recentStops" :key="stop.at">
                    <span class="cadence-dot" aria-hidden="true"></span>
                    <span class="cadence-date">{{ formatDate(stop.at) }}</span>
                    <span class="cadence-reading">{{ hours(stop.hours) }}</span>
                  </li>
                </ul>
              </div>

              <!-- Customer self-report. Recorded as customer-reported until the next verified visit. -->
              <div v-if="!isDemo && machine.hoursEntry?.enabled !== false" class="self-report">
                <form class="self-report-form" novalidate @submit.prevent="submitReading">
                  <label for="reported-hours">Report the hour meter</label>
                  <div class="self-report-row">
                    <input id="reported-hours" v-model.number="reading" type="number" inputmode="decimal" step="0.1" min="0" required :disabled="busy" />
                    <button class="btn btn-primary" type="submit" :disabled="busy">{{ busy ? 'Saving…' : 'Enter' }}</button>
                  </div>
                  <label class="grease-check"><input v-model="greased" type="checkbox" /><span>I greased the machine</span></label>
                  <p v-if="error" class="report-error" role="alert">{{ error }}</p>
                </form>
                <div v-if="lastReadingId" class="report-done">
                  <p role="status">{{ status }}</p>
                  <button class="report-undo" type="button" @click="undoReading">Undo</button>
                </div>
                <p v-else-if="status" class="report-status" role="status">{{ status }}</p>
              </div>
            </article>
          </section>

          <p v-if="historyOpen && !historySheetProps" class="loading">{{ error || 'Loading service history…' }}</p>
          <HistorySheet
            v-else-if="historyOpen"
            ref="historySheet"
            v-bind="historySheetProps"
            :meter-unit="meterUnit"
            :footer-note="isDemo ? 'Example record for demonstration purposes.' : 'Maintenance and repair history recorded by Centerfire Services.'"
            @close="historyOpen = false"
          />

          <section class="record-panel data-sheet">
            <div class="panel-heading data-heading">
              <div>
                <p>Machine data sheet</p>
                <h2>Service specifications</h2>
              </div>
              <div class="zerk-count">
                <span>Grease zerks</span>
                <strong>{{ machine.machineSpecs.greasePoints ?? '—' }}</strong>
              </div>
            </div>

            <!-- Filters and part numbers are deliberately NOT shown here: they read as noise in
                 the portal and the number can be read off the filter on the machine. Fluids stay,
                 because the customer needs them to top off between our stops. -->
            <div v-if="machine.machineSpecs.fluids.length" class="tables-grid single">
              <div class="data-table-wrap">
                <h3>Fluids</h3>
                <p class="table-note">Use these if you need to top off between our visits.</p>
                <table>
                  <thead>
                    <tr>
                      <th>System</th>
                      <th>Fluid type</th>
                      <th v-if="hasCapacities">Capacity</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="fluid in machine.machineSpecs.fluids" :key="fluid.system">
                      <td>{{ fluid.system }}</td>
                      <td>{{ fluid.specification }}<span v-if="fluid.dealerSpec" class="dealer-spec">{{ fluid.dealerSpec }}</span></td>
                      <td v-if="hasCapacities">{{ fluid.capacity || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
                <p v-for="caution in fluidCautions" :key="caution" class="coolant-caution">{{ caution }}</p>
              </div>
            </div>

            <div v-if="machine.documents.length" class="documents">
              <a v-for="document in machine.documents" :key="document.url" :href="document.url" target="_blank" rel="noopener noreferrer">{{ document.label }}</a>
            </div>
          </section>
        </article>
      </template>

      <section v-else class="unlock-card"><h1>Record unavailable</h1><p>{{ error || 'We could not open that machine record.' }}</p><button class="btn btn-primary" @click="lock">Try the PIN again</button></section>
    </section>
  </main>
</template>

<script>
import HistorySheet from '@/components/HistorySheet.vue';
import BasicMachineLanding from '@/components/BasicMachineLanding.vue';
import { adminOrigin } from '@/data/siteOrigins';
import { findDemoUnit, clearPortalSession, hasPortalSession, loadHistorySheet, loadMachineTagsResult, loadPortalFleet, loadPortalMachine, portalScope, submitHourReading, undoHourReading, unlockPortal } from '@/data/maintainedUnits';

function demoMachine(unit) {
  // Demo countdowns recompute from the due targets so the marketing records never go stale,
  // matching how the pre-rebuild page computed them at render time.
  const currentHours = unit.reading?.hours;
  const remaining = (dueAtHours) => (Number.isFinite(dueAtHours) && Number.isFinite(currentHours) ? Math.round((dueAtHours - currentHours) * 10) / 10 : null);
  const daysUntil = (date) => { const parsed = new Date(date); return Number.isNaN(parsed.valueOf()) ? null : Math.max(0, Math.ceil((parsed.getTime() - Date.now()) / 86400000)); };
  return {
    identity: { tag: String(unit.lookupNumber), publicLabel: unit.publicLabel, make: unit.make, model: unit.model, serial: unit.serial || null, greeting: unit.greeting },
    nextOilChange: unit.oilDue ? { dueHours: unit.oilDue.dueAtHours, dueDate: unit.oilDue.date, remainingHours: remaining(unit.oilDue.dueAtHours), remainingDays: unit.oilDue.date ? daysUntil(unit.oilDue.date) : null } : null,
    currentStatus: { reading: unit.reading ? { hours: unit.reading.hours, at: unit.verifiedReading?.date, source: 'verified' } : null, lastVerifiedReading: unit.verifiedReading ? { hours: unit.verifiedReading.hours, at: unit.verifiedReading.date, source: 'verified' } : null, programStatus: unit.programStatus || 'active' },
    lastService: unit.lastService ? { at: unit.lastService.date, hours: Number.parseFloat(String(unit.lastService.reading || '').replace(/[^\d.]/g, '')) || null, title: unit.lastService.title, detail: unit.lastService.detail } : null,
    lastMaintenanceStop: unit.lastService ? { at: unit.lastService.date, hours: Number.parseFloat(String(unit.lastService.reading || '').replace(/[^\d.]/g, '')) || null, unit: unit.reading?.unit || 'hours' } : null,
    lastOilChange: unit.lastOilChange ? { at: unit.lastOilChange.date, hours: unit.lastOilChange.hours ?? null, unit: unit.lastOilChange.unit || 'hours' } : null,
    previousRepair: unit.previousRepair ? { id: 'demo-repair', at: unit.previousRepair.date, hours: null, title: unit.previousRepair.title, detail: unit.previousRepair.detail } : null,
    upcomingService: unit.upcomingService ? { item: unit.upcomingService.item, remainingHours: remaining(unit.upcomingService.dueAtHours), remainingDays: null } : null,
    greaseSchedule: { frequency: unit.checkupSchedule?.frequency, nextGreaseDate: unit.checkupSchedule?.nextGreaseDate || null, recentStops: (unit.greaseStops || []).map((stop) => ({ at: stop.date, hours: Number.parseFloat(stop.reading) || null })), ownerActivity: { count: unit.ownerGreases?.count || 0, since: unit.ownerGreases?.since || null } },
    history: (unit.history || []).map((entry, index) => ({ id: `demo-${index}`, at: entry.date, hours: Number.parseFloat(String(entry.reading || '').replace(/[^\d.]/g, '')) || null, category: entry.category, title: entry.work, detail: entry.notes })),
    machineSpecs: { greasePoints: unit.dataSheet?.greaseZerks || null, fluids: unit.dataSheet?.fluids || [], coolantCaution: unit.dataSheet?.coolantCaution || null }, documents: [],
  };
}

function adaptPortalMachine(machine) {
  const reading = machine.currentReading ? { hours: machine.currentReading.value, at: machine.currentReading.at, source: machine.currentReading.source, unit: machine.currentReading.unit } : null;
  const history = (machine.history || []).map((event) => ({ id: event.id, at: event.at, hours: event.reading?.value ?? null, unit: event.reading?.unit, category: 'Maintenance', title: event.title, detail: '' }));
  const repairs = (machine.activeRepairs || []).map((repair) => ({ id: repair.id, at: null, hours: null, category: 'Repair', title: repair.resolution ? 'Repair completed' : 'Repair noted', detail: repair.resolution || repair.problem || '' }));
  return {
    ...machine,
    nextOilChange: machine.nextOilChange ? { ...machine.nextOilChange, remainingHours: machine.nextOilChange.hoursRemaining, remainingDays: machine.nextOilChange.daysRemaining } : null,
    currentStatus: { reading, lastVerifiedReading: reading?.source !== 'portal' ? reading : null, programStatus: 'active' },
    lastService: history[0] ? { at: history[0].at, hours: history[0].hours, title: history[0].title, detail: history[0].detail } : null,
    previousRepair: repairs[0] || null,
    upcomingService: machine.upcomingServices?.[0] ? { item: machine.upcomingServices[0].label, ...machine.upcomingServices[0], remainingHours: machine.upcomingServices[0].hoursRemaining, remainingDays: machine.upcomingServices[0].daysRemaining } : null,
    // The API now derives this from the stops on the record; the panel used to be blanked here.
    greaseSchedule: { frequency: null, nextGreaseDate: null, recentStops: [], ownerActivity: { count: 0, since: null }, ...(machine.greaseSchedule || {}) },
    history: [...history, ...repairs],
  };
}

export default {
  name: 'MaintainedUnitAccessView',
  components: {
    BasicMachineLanding,
    HistorySheet,
  },
  data: () => ({ locked: true, busy: false, checkingEnrollment: false, kind: '', scanLabel: '', scanType: '', pin: '', error: '', status: '', fleet: [], machine: null, isDemo: false, reading: null, greased: false, lastReadingId: '', historyOpen: false, sheet: null }),
  computed: {
    staffLink() { return `${adminOrigin}${this.$route.path}`; },
    routeTag() { return this.$route.params.unitId ? String(this.$route.params.unitId) : ''; },
    portalToken() { return this.$route.params.portalToken ? String(this.$route.params.portalToken) : ''; },
    machineToken() { return this.$route.params.machineToken ? String(this.$route.params.machineToken) : ''; },
    meterUnit() { return this.machine?.currentStatus?.reading?.unit === 'miles' ? 'miles' : 'hours'; },
    hasCapacities() { return (this.machine?.machineSpecs?.fluids || []).some((fluid) => fluid.capacity); },
    fluidCautions() {
      const specs = this.machine?.machineSpecs;
      if (!specs) return [];
      return [...new Set([specs.coolantCaution, ...(specs.fluids || []).map((fluid) => fluid.caution)].filter(Boolean))];
    },
    // Date always, hours only when the change was logged with a reading — an honest "as best we can."
    lastOilChangeText() {
      const change = this.machine?.lastOilChange;
      if (!change) return 'Last oil change: not yet on our digital record';
      const when = this.formatDate(change.at);
      return typeof change.hours === 'number' ? `Last oil change: ${when} · ${change.hours.toLocaleString()} ${change.unit || 'hours'}` : `Last oil change: ${when}`;
    },
    /**
     * Both tiers print the same document. The demo record is the one exception: it is marketing
     * copy that never touches the API, so it is adapted into the same shape here.
     */
    historySheetProps() {
      if (this.isDemo) {
        return {
          identity: this.machine.identity,
          currentHours: this.machine.currentStatus.reading?.hours ?? null,
          rows: this.machine.history,
          ownerGreasings: this.machine.greaseSchedule?.ownerActivity || null,
        };
      }
      if (!this.sheet) return null;
      return {
        identity: this.sheet.identity,
        currentHours: this.sheet.currentReading?.value ?? null,
        rows: this.sheet.history,
        ownerGreasings: this.sheet.ownerGreasings,
      };
    },
  },
  async created() { await this.dispatch(); },

  watch: {
    // A URL change can cross tiers — basic to program, demo to real — so re-classify instead of
    // assuming the screen already open still fits the machine now in the address bar. Only
    '$route.fullPath': {
      async handler() {
        this.isDemo = false; this.kind = ''; this.locked = true; this.machine = null; this.fleet = [];
        this.sheet = null; this.historyOpen = false; this.error = ''; this.status = ''; this.lastReadingId = '';
        this.scanLabel = ''; this.scanType = '';
        // Digits typed against one machine's card must not carry to the next one's.
        this.pin = ''; this.busy = false; this.reading = null; this.greased = false;
        await this.dispatch();
      },
    },
  },
  methods: {
    /**
     * The customer scan dispatcher:
     *
     *   program machine → the existing PIN unlock and full fleet portal, unchanged
     *   basic machine   → the three-button landing
     *   unknown tag     → the plans page, unchanged
     *
     * A token link cannot be classified from the public directory on purpose — untagged
     * machines publish no token — so it opens the landing and the unlock response decides
     * the tier.
     *
     * A tagged route is CLASSIFIED BEFORE any stored session is consulted, and that order
     * matters. Checking the session first meant a leftover fleet token — expired, or minted
     * for a different customer in the same tab — sent a basic machine to the program PIN card
     * instead of its landing, where the correct PIN could never open anything (the unlock
     * returns history scope and the card only knew how to read the fleet). A session belonging
     * to someone else says nothing about the machine in the URL.
     */
    async dispatch() {
      const demo = this.routeTag && findDemoUnit(this.routeTag);
      if (demo) { this.machine = demoMachine(demo); this.fleet = [this.machine]; this.isDemo = true; this.locked = false; return; }

      if (this.machineToken) {
        if (this.hasFleetSession()) { await this.loadRecords(); return; }
        this.kind = 'basic';
        return;
      }
      if (this.routeTag) {
        this.checkingEnrollment = true;
        const result = await loadMachineTagsResult();
        this.checkingEnrollment = false;
        const machine = result.error ? null : result.tags.find((item) => item.lookupNumber === this.routeTag);
        // An unknown tag still tells the scanner nothing about whether it exists.
        if (!result.error && !machine) { await this.$router.replace('/managed-maintenance-explained'); return; }
        if (machine && machine.kind !== 'program') {
          this.kind = 'basic';
          this.scanLabel = machine.label || '';
          this.scanType = machine.publicType || '';
          return;
        }
        // Directory unreachable: fall through to the session and PIN path, which handles either
        // tier now, rather than stranding the visitor on an error they cannot act on.
        if (result.error && !this.hasFleetSession()) this.error = result.error;
      }
      if (this.hasFleetSession()) await this.loadRecords();
    },
    // A history-scoped session cannot read the fleet, so it must not trigger a portal load.
    hasFleetSession() { return hasPortalSession() && portalScope() === 'fleet'; },
    async openFleetRecords() { this.kind = ''; await this.loadRecords(); },
    /**
     * A correct PIN on a basic machine mints a history-scoped session, which cannot read the
     * fleet. Calling loadRecords() regardless was the dead end: unlock 200, fleet 403, back to
     * the PIN form with an error, on every attempt, until the unlock rate limit took over.
     */
    async unlock() {
      this.busy = true;
      this.error = '';
      const result = await unlockPortal({ tag: this.routeTag || undefined, portalToken: this.portalToken || undefined, pin: this.pin });
      if (result.error) { this.error = result.error; this.busy = false; return; }
      this.pin = '';
      if (result.scope === 'history') { await this.openHistoryTier(); return; }
      await this.loadRecords();
    },
    /** Hand a history-scoped session to the basic landing with its sheet already open. */
    async openHistoryTier() {
      const key = this.routeTag || this.machineToken;
      const record = await loadHistorySheet(key);
      // The visitor can navigate while the sheet is in flight; the route that arrived after this
      // one owns the screen, and the watcher has already reset the state it needs.
      if ((this.routeTag || this.machineToken) !== key) return;
      this.busy = false;
      if (record.error) { this.error = record.error; return; }
      this.sheet = record.sheet;
      this.scanLabel = this.scanLabel || record.sheet.identity?.publicLabel || '';
      this.scanType = this.scanType || record.sheet.identity?.publicType || '';
      this.locked = false;
      this.kind = 'basic';
    },
    async loadRecords() {
      this.busy = true;
      this.error = '';
      const fleet = await loadPortalFleet();
      if (fleet.error) {
        this.busy = false;
        // A session that cannot read the fleet is expired, another customer's, or the wrong
        // tier for this machine. Drop it and let classification pick the screen instead of
        // leaving a PIN card up that the right PIN cannot get past.
        if (fleet.status === 401 || fleet.status === 403) {
          clearPortalSession();
          // Never hand a sheet fetched for an earlier screen to whatever dispatch renders next.
          this.sheet = null;
          await this.dispatch();
          if (!this.kind && !this.machine) { this.locked = true; this.error = fleet.error; }
          return;
        }
        this.error = fleet.error;
        return;
      }
      this.fleet = fleet.machines;
      const tag = this.routeTag || this.machine?.identity?.tag || this.fleet[0]?.tag;
      if (!tag) { this.error = 'No active machines are assigned to this customer.'; this.locked = false; this.busy = false; return; }
      const record = await loadPortalMachine(tag);
      if (record.error) { this.error = record.error; this.busy = false; return; }
      this.sheet = null;
      this.machine = adaptPortalMachine(record.machine);
      this.reading = record.machine.currentReading?.value ?? null;
      this.locked = false;
      this.busy = false;
    },
    async openFleetMachine(tag) { this.busy = true; const record = await loadPortalMachine(tag); this.busy = false; if (record.error) { this.error = record.error; return; } this.sheet = null; this.historyOpen = false; this.machine = adaptPortalMachine(record.machine); this.reading = record.machine.currentReading?.value ?? null; },
    lock() { clearPortalSession(); this.kind = ''; this.sheet = null; this.historyOpen = false; this.locked = true; this.machine = null; this.fleet = []; this.pin = ''; this.error = ''; this.status = ''; },
    async submitReading() { this.busy = true; this.error = ''; const tag = this.machine.identity.tag; const result = await submitHourReading({ tag, hours: Number(this.reading), greased: this.greased }); this.busy = false; if (result.error) { this.error = result.error; return; } this.lastReadingId = result.id; this.status = 'Reading saved. Thank you.'; await this.openFleetMachine(tag); },
    async undoReading() { this.busy = true; const tag = this.machine.identity.tag; const result = await undoHourReading({ tag, id: this.lastReadingId }); this.busy = false; if (result.error) { this.error = result.error; return; } this.lastReadingId = ''; this.status = 'That reading was withdrawn.'; await this.openFleetMachine(tag); },
    formatDate(value) { if (!value) return '—'; const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value); const date = new Date(dateOnly ? `${value}T12:00:00Z` : value); return Number.isNaN(date.valueOf()) ? value : new Intl.DateTimeFormat('en-US', { dateStyle: 'long', ...(dateOnly ? { timeZone: 'UTC' } : {}) }).format(date); },
    hours(value) { return typeof value === 'number' ? `${value.toLocaleString()} hours` : 'Pending'; },
    stopHours(stop) { return typeof stop?.hours === 'number' ? `${stop.hours.toLocaleString()} ${stop.unit || 'hours'} · maintenance run` : 'Maintenance run'; },
    meterValue(value) { return typeof value === 'number' ? `${value.toLocaleString()} ${this.meterUnit}` : 'Pending'; },
    dueHeadline(due) { return due.dueHours !== null ? `Due at ${due.dueHours.toLocaleString()} ${this.meterUnit}` : `Due ${this.formatDate(due.dueDate)}`; },
    // "whichever comes first" reads wrong once the calendar date has passed; the pill carries
    // the calm due-now message instead.
    dueSubline(due) { return due.dueHours !== null && due.dueDate && !(typeof due.remainingDays === 'number' && due.remainingDays < 0) ? `or ${this.formatDate(due.dueDate)}, whichever comes first` : ''; },
    // No overdue language and no negative numbers in the portal — past-due work is flagged to
    // us on the back end; the customer sees what is left, or a calm "we're on it."
    remainingText(due) { const values = []; if (typeof due.remainingHours === 'number' && due.remainingHours >= 0) values.push(`${Math.round(due.remainingHours * 10) / 10} ${this.meterUnit} remaining`); if (typeof due.remainingDays === 'number' && due.remainingDays >= 0) values.push(`${due.remainingDays} days remaining`); if (values.length) return values.join(' · '); const pastDue = (typeof due.remainingHours === 'number' && due.remainingHours < 0) || (typeof due.remainingDays === 'number' && due.remainingDays < 0); return pastDue ? 'Due now — we\u2019re getting it scheduled' : 'Schedule being confirmed'; },
    async toggleHistory() {
      this.historyOpen = !this.historyOpen;
      if (!this.historyOpen) return;
      if (!this.isDemo && !this.sheet) {
        const record = await loadHistorySheet(this.machine.identity.tag || this.routeTag || this.machineToken);
        if (record.error) { this.error = record.error; return; }
        this.sheet = record.sheet;
      }
      this.$nextTick(() => this.$refs.historySheet?.focus());
    },
  },
};
</script>

<style scoped>
/* Page chrome: the pre-rebuild atmosphere — orange glow up top, tall pad for the PIN screen,
   and a tighter pad once the record is open. */
.record-page { min-height: 78vh; padding: clamp(10rem,15vw,12rem) 1rem 5rem; background: radial-gradient(circle at 50% 12%,rgba(255,90,2,.14),transparent 28%),var(--black); color: white; }
.record-page.has-record { padding-top: clamp(2.5rem,4vw,3.5rem); }
.staff-link { margin-left: 1rem; }
.record-shell { width: min(1180px,100%); margin: auto; }.back, .staff-link { display: inline-block; margin-bottom: 1.5rem; color: #bbb; }.eyebrow { margin: 0; color: var(--orange); font: 700 .7rem ui-monospace,monospace; letter-spacing: .14em; text-transform: uppercase; }
.shield { display: none; }
.unlock-card { width: min(100%,600px); margin: 2rem auto; padding: clamp(1.5rem,5vw,3rem); border: 1px solid #444; border-top: 3px solid var(--orange); background: #111; }.unlock-card h1 { margin: .4rem 0; font-size: clamp(2.5rem,8vw,6rem); text-transform: uppercase; }.unlock-card > p { color: #bbb; }.unlock-card form { display: grid; grid-template-columns: minmax(0,1fr) auto; gap: .7rem; align-items: end; margin-top: 1.5rem; }.unlock-card label { display: grid; gap: .35rem; color: #bbb; font-size: .82rem; }input { min-height: 48px; padding: .7rem; border: 1px solid #666; background: #080808; color: white; font: inherit; }
.fleet-nav { display: flex; gap: .5rem; overflow-x: auto; margin-bottom: 1rem; padding: .7rem; border: 1px solid rgba(255,255,255,.22); background: rgba(0,0,0,.6); }.fleet-nav p { min-width: max-content; margin: auto .5rem; color: var(--orange); text-transform: uppercase; }.fleet-nav button { min-width: 165px; display: grid; gap: .15rem; padding: .65rem; border: 1px solid rgba(255,255,255,.3); background: rgba(255,255,255,.04); color: white; text-align: left; cursor: pointer; }.fleet-nav button.active { border-color: var(--orange); }.fleet-nav span { color: #aaa; font-size: .72rem; }
.status { min-height: 1.2em; color: #9bcd9b; }.status.error { color: #ff9a67; }.loading { padding: 2rem; color: #aaa; text-align: center; }

/* ── The restored record dashboard (from ec39f6f) ─────────────────────────────────── */
.record-dashboard { display: grid; gap: 1rem; }
/* Greeting is set per portal by hand — a company reads differently from a personal owner.
   Sentence case: a greeting reads as a greeting, not a label. The machine name still gets
   uppercase when it stands in as the hero. */
.greeting-hero { margin: 0 0 1.8rem; font-size: clamp(2.6rem,6vw,5.4rem); line-height: .95; text-align: center; }
.greeting-hero.hero-machine { text-transform: uppercase; }
.record-header { display: flex; justify-content: space-between; gap: 2rem; align-items: end; padding-bottom: 1.4rem; border-bottom: 1px solid rgba(255,255,255,.18); }
.machine-identity { padding: .6rem 1rem; border: 1px solid rgba(255,255,255,.22); }
.record-header h2 { margin: .2rem 0 .15rem; font-size: clamp(1.4rem,2.8vw,2.1rem); line-height: 1.05; text-transform: uppercase; }
.record-header > div > p:last-child { color: rgba(255,255,255,.72); font-size: .92rem; }
.demo-label,.panel-heading > p,.oil-due-card > p:first-child,.entry-type { color: var(--orange); font-family: ui-monospace,'SFMono-Regular',Menlo,Consolas,monospace; font-size: .72rem; font-weight: 500; letter-spacing: .12em; text-transform: uppercase; }
.header-right { display: grid; justify-items: end; gap: .7rem; }
/* The tag number is what is physically on the machine and what the QR scans to. */
.machine-number { display: grid; justify-items: end; gap: .15rem; padding: .5rem .85rem; border: 1px solid rgba(255,255,255,.22); }
.machine-number span { font-size: .68rem; letter-spacing: .09em; text-transform: uppercase; opacity: .72; }
.machine-number strong { font-size: 1.6rem; line-height: 1; letter-spacing: .05em; color: var(--orange); }
.lock-button { flex: 0 0 auto; border: 1px solid #666; padding: .55rem .75rem; background: transparent; color: white; cursor: pointer; }

.oil-due-card { max-width: 820px; width: 100%; box-sizing: border-box; justify-self: center; display: grid; gap: .45rem; padding: clamp(1.5rem,4vw,2.5rem); border: 2px solid var(--orange); background: linear-gradient(145deg,rgba(255,90,2,.14),transparent 62%); text-align: center; }
.oil-due-card h2 { font-size: clamp(2rem,5vw,4rem); font-weight: 400; line-height: 1; text-transform: uppercase; }
.oil-due-card > p:nth-of-type(2) { color: rgba(255,255,255,.78); }
.oil-due-card span { justify-self: center; margin-top: .45rem; padding: .4rem .75rem; background: var(--orange); color: var(--black); font-weight: 600; }
.oil-due-card .last-oil { margin-top: .5rem; font-size: .8rem; letter-spacing: .06em; text-transform: uppercase; color: rgba(255,255,255,.55); }

.record-stats { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 1rem; }
.record-stats > div { min-height: 145px; display: grid; align-content: start; gap: .35rem; padding: 1.25rem; border: 1px solid rgba(255,255,255,.23); border-radius: var(--radius-sm); }
.record-stats p { color: rgba(255,255,255,.62); font-size: .78rem; letter-spacing: .08em; text-transform: uppercase; }
.record-stats strong { font-family: 'Jost','Barlow',sans-serif; font-size: clamp(1.35rem,2.7vw,2rem); font-weight: 400; }
.record-stats span { color: rgba(255,255,255,.67); font-size: .85rem; }

.record-grid { display: grid; grid-template-columns: minmax(0,1.3fr) minmax(280px,.7fr); gap: 1rem; }
.record-panel { padding: clamp(1.3rem,3vw,2rem); border: 1px solid rgba(255,255,255,.23); border-radius: var(--radius); background: rgba(0,0,0,.74); }
.panel-heading h2 { margin-top: .35rem; font-size: clamp(1.7rem,3vw,2.5rem); font-weight: 400; }
.history-entry { display: grid; gap: .5rem; margin-top: 1.3rem; padding-top: 1.3rem; border-top: 1px solid rgba(255,255,255,.16); }
.history-entry h3 { font-size: 1.2rem; font-weight: 500; }
.history-entry > p:last-child,.upcoming-panel > p:last-child { color: rgba(255,255,255,.77); }
.entry-meta { color: rgba(255,255,255,.58); font-size: .86rem; }
.upcoming-panel { border-color: rgba(255,90,2,.62); background: linear-gradient(145deg,rgba(255,90,2,.1),transparent 56%); }
.history-button { justify-self: start; margin-top: 1.4rem; }
.remaining { width: fit-content; margin: 1rem 0; padding: .35rem .6rem; border: 1px solid var(--orange); color: var(--orange); font-weight: 600; }

.checkup-schedule { display: grid; gap: .35rem; margin-top: 1.5rem; padding-top: 1.2rem; border-top: 1px solid rgba(255,255,255,.18); }
.checkup-schedule > p { color: var(--orange); font-family: ui-monospace,'SFMono-Regular',Menlo,Consolas,monospace; font-size: .72rem; font-weight: 500; letter-spacing: .1em; text-transform: uppercase; }
.checkup-schedule h3 { margin-top: .35rem; font-size: 1rem; font-weight: 500; }
.checkup-schedule strong { font-family: 'Jost','Barlow',sans-serif; font-size: 1.55rem; font-weight: 400; }
.checkup-schedule span { color: rgba(255,255,255,.6); font-size: .8rem; }
.owner-greases { margin-top: 1.4rem; padding: .6rem .85rem; border-left: 3px solid var(--orange); background: rgba(255,255,255,.04); font-size: .9rem; }
.owner-greases strong { color: var(--orange); }
.grease-cadence { margin-top: 1.5rem; }
.grease-cadence h3 { margin-bottom: .6rem; font-size: .72rem; letter-spacing: .09em; text-transform: uppercase; opacity: .72; }
.grease-cadence ul { display: grid; gap: .45rem; margin: 0; padding: 0; list-style: none; }
.grease-cadence li { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: .6rem; font-size: .88rem; }
.cadence-dot { width: .5rem; height: .5rem; border-radius: 50%; background: var(--orange); }
.cadence-reading { opacity: .7; font-variant-numeric: tabular-nums; }

/* Customer self-report — the value of this is frequency: one reading a month from us versus a
   reading every time the owner touches it. */
.self-report { margin-top: 1.6rem; padding-top: 1.4rem; border-top: 1px solid rgba(255,255,255,.15); }
.self-report-form label { display: block; margin-bottom: .5rem; font-size: .8rem; letter-spacing: .06em; text-transform: uppercase; opacity: .8; }
.self-report-row { display: grid; grid-template-columns: 1fr auto; gap: .6rem; }
.self-report-row input { min-width: 0; padding: .7rem .8rem; border: 1px solid rgba(255,255,255,.3); background: rgba(0,0,0,.4); color: inherit; font: inherit; }
.grease-check { display: flex; align-items: center; gap: .55rem; margin-top: .85rem; cursor: pointer; font-size: .92rem; }
.grease-check input { width: 1.15rem; height: 1.15rem; min-height: 0; accent-color: var(--orange); cursor: pointer; }
.report-error { margin-top: .7rem; color: var(--orange); font-size: .85rem; }
.report-status { margin-top: .7rem; color: #9bcd9b; font-size: .88rem; }
.report-done { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-top: .85rem; padding: .75rem .9rem; border: 1px solid var(--orange); border-left-width: 4px; font-size: .88rem; }
.report-done p { margin: 0; }
.report-undo { flex-shrink: 0; padding: .35rem .9rem; border: 1px solid rgba(255,255,255,.4); background: none; color: inherit; font: inherit; cursor: pointer; }
.report-undo:hover { border-color: var(--orange); color: var(--orange); }

.data-heading { display: flex; justify-content: space-between; gap: 2rem; align-items: center; }
.zerk-count { min-width: 145px; display: grid; gap: .25rem; padding: .8rem 1rem; border-left: 2px solid var(--orange); text-align: right; }
.zerk-count span { color: rgba(255,255,255,.62); font-size: .75rem; letter-spacing: .08em; text-transform: uppercase; }
.zerk-count strong { font-family: 'Jost','Barlow',sans-serif; font-size: 1.8rem; font-weight: 400; }
.tables-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 1.5rem; margin-top: 1.5rem; }
/* Fluids is the only table now that filter numbers are off the portal. */
.tables-grid.single { grid-template-columns: minmax(0,1fr); }
.table-note { margin: -.35rem 0 .85rem; font-size: .85rem; opacity: .72; }
/* The dealer product sits under the universal spec: buy-anywhere first, brand as the
   cross-reference. */
.dealer-spec { display: block; margin-top: .2rem; font-size: .82rem; opacity: .65; }
.coolant-caution { margin-top: 1.1rem; padding: .85rem 1rem; border: 1px solid var(--orange); border-left-width: 4px; font-size: .88rem; line-height: 1.5; }
.data-table-wrap { min-width: 0; overflow-x: auto; }
.data-table-wrap h3 { margin-bottom: .65rem; font-size: 1rem; font-weight: 500; }
.data-table-wrap table { width: 100%; border-collapse: collapse; font-size: .88rem; }
.data-table-wrap th,.data-table-wrap td { padding: .75rem .6rem; border-bottom: 1px solid rgba(255,255,255,.15); text-align: left; vertical-align: top; }
.data-table-wrap th { color: var(--orange); font-size: .7rem; letter-spacing: .08em; text-transform: uppercase; }
.data-table-wrap td:last-child { color: rgba(255,255,255,.74); }
.documents { margin-top: 1.5rem; display: grid; gap: .5rem; }.documents a { color: var(--orange); }

/* CFM# 0101 keeps its bespoke PIN screen; the record itself now uses the restored default. */
.legacy-0101 .back { display: none; }
.legacy-0101 .staff-link { margin-left: 0; }
.legacy-0101 .unlock-card { width: 100%; box-sizing: border-box; display: grid; grid-template-columns: minmax(0,1.2fr) minmax(340px,.8fr); grid-template-areas: "shield form" "eyebrow form" "title form" "copy form" "status form"; gap: .8rem clamp(3rem,7vw,6rem); margin: 0; padding: 0; border: 0; background: transparent; }
.legacy-0101 .unlock-card .shield { grid-area: shield; position: relative; display: block; width: 52px; height: 60px; box-sizing: border-box; border: 2px solid var(--orange); border-radius: 48% 48% 56% 56% / 32% 32% 68% 68%; }
.legacy-0101 .shield span { position: absolute; top: 24px; left: 18px; width: 12px; height: 12px; border: 2px solid var(--orange); border-radius: 50%; }
.legacy-0101 .shield span::after { content: ''; position: absolute; top: 8px; left: 3px; width: 2px; height: 9px; background: var(--orange); }
.legacy-0101 .unlock-card > .eyebrow { grid-area: eyebrow; }
.legacy-0101 .unlock-card h1 { grid-area: title; max-width: 12ch; margin: 0; font-size: clamp(2.8rem,6.5vw,5.6rem); line-height: .96; }
.legacy-0101 .unlock-card > p:not(.eyebrow,.status) { grid-area: copy; max-width: 52ch; margin: 0; color: white; font-size: clamp(1rem,1.5vw,1.15rem); }
.legacy-0101 .unlock-card form { grid-area: form; width: min(100%,420px); box-sizing: border-box; justify-self: end; align-self: center; display: grid; grid-template-columns: 1fr; gap: 1rem; margin: 0; padding: clamp(1.4rem,4vw,2.4rem); border: 1px solid rgba(255,255,255,.28); border-top: 2px solid var(--orange); border-radius: var(--radius); background: rgba(0,0,0,.78); }
.legacy-0101 .unlock-card form label { color: white; font-weight: 600; }
.legacy-0101 .unlock-card form input { width: 100%; box-sizing: border-box; min-height: 54px; border-color: rgba(255,255,255,.52); border-radius: 5px; font-size: 1.25rem; letter-spacing: .18em; }
.legacy-0101 .unlock-card form .btn { width: 100%; box-sizing: border-box; }
.legacy-0101 .unlock-card .status { grid-area: status; }

@media (max-width: 960px) {
  .record-grid,.tables-grid,.record-stats { grid-template-columns: 1fr; }
}
@media (max-width: 620px) {
  .record-header,.data-heading { display: grid; align-items: start; }
  .lock-button { justify-self: start; }
  .header-right { justify-items: start; }
  .machine-number { justify-items: start; }
  .zerk-count { width: 100%; box-sizing: border-box; border-left: 0; border-top: 2px solid var(--orange); text-align: left; }
  .unlock-card form,.self-report-row { grid-template-columns: 1fr; }
  .legacy-0101 .unlock-card { grid-template-columns: 1fr; grid-template-areas: "shield" "eyebrow" "title" "copy" "form" "status"; }
  .legacy-0101 .unlock-card form { width: 100%; justify-self: stretch; }
}
@media print {
  :global(.site-nav),:global(.site-footer) { display: none !important; }
  :global(body),.record-page { background: #fff !important; }
  .record-page { min-height: 0; padding: 0 !important; color: #111; }
  .record-shell { width: 100%; }
  /* Keep the sheet itself visible; its interior print rules live in HistorySheet.vue. */
  .record-shell > :not(.record-dashboard),.record-dashboard > :not(.print-history) { display: none !important; }
  .record-dashboard { display: block !important; }
  .print-history { display: block !important; }
}
</style>
